import type {
  StaffIdentity,
  StaffMember,
  StaffMembers,
} from '@/types/control/staff';

import { controlApiRequest } from './control-api-client';

/**
 * `GET /api/v1/me` envelope. Fields are nested under `user`, not flat — reading
 * `body.is_superuser` instead of `body.user.is_superuser` yields `undefined`,
 * which is falsy, so the mistake presents as "nobody is staff" rather than as
 * an error.
 */
type MeResponse = {
  user?: {
    id?: unknown;
    email?: unknown;
    name?: unknown;
    tenant_id?: unknown;
    is_superuser?: unknown;
    is_tenant_creator?: unknown;
  };
};

function asString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function asBoolean(value: unknown): boolean {
  return value === true;
}

/**
 * `/api/admin/staff` (HYD-2112, control PR #1822). Every route is
 * superuser-gated on the server, so a tenant_creator who reached the admin
 * area through `/api/v1/me` is still a `403` here; `503 {"error": ...}` means
 * the staff store is not wired.
 */
const STAFF = '/api/admin/staff';
const TENANT_CREATOR = `${STAFF}/tenant_creator`;

type MemberResponse = {
  email?: unknown;
  group?: unknown;
  granted_by?: unknown;
  granted_at?: unknown;
};

function toMember(res: MemberResponse): StaffMember {
  return {
    email: asString(res.email),
    group: res.group === 'superuser' ? 'superuser' : 'tenant_creator',
    grantedBy: asString(res.granted_by),
    grantedAt: asString(res.granted_at),
  };
}

function asMembers(value: unknown): StaffMember[] {
  return Array.isArray(value)
    ? value.map((item) => toMember(item as MemberResponse))
    : [];
}

export const controlStaffService = {
  /**
   * Read the current user's staff standing from control-api.
   *
   * This route is authenticated-only with no role requirement, so an ordinary
   * non-staff user gets a `200` with both booleans `false` rather than a `403`.
   * A `401` is the only "not signed in" signal and is left to the caller, which
   * sends the user to Control's login.
   *
   * `is_tenant_creator` is added by T02 (HYD-1363). Until that lands the field
   * is absent and every user reads as not-a-tenant-creator, which is the safe
   * direction: it withholds access rather than granting it.
   */
  async getIdentity(signal?: AbortSignal): Promise<StaffIdentity> {
    const res = await controlApiRequest<MeResponse>({
      path: '/api/v1/me',
      signal,
    });

    const user = res?.user ?? {};

    return {
      userId: asString(user.id),
      email: asString(user.email),
      name: asString(user.name),
      tenantId: asString(user.tenant_id),
      isSuperuser: asBoolean(user.is_superuser),
      isTenantCreator: asBoolean(user.is_tenant_creator),
    };
  },

  /**
   * Both staff groups. The contract says both arrays are always present and
   * never null; the guard only keeps a malformed body from crashing the page,
   * reading it as "no members", which withholds rather than grants.
   */
  async listMembers(signal?: AbortSignal): Promise<StaffMembers> {
    const res = await controlApiRequest<{
      superuser?: unknown;
      tenant_creator?: unknown;
    } | null>({ path: STAFF, signal });

    return {
      superuser: asMembers(res?.superuser),
      tenantCreator: asMembers(res?.tenant_creator),
    };
  },

  /**
   * Grant tenant_creator to one email.
   *
   * Trimmed but not lowercased: the server normalizes case and validates the
   * address (a bare `name@example.com`, no display name), and its `400` detail
   * is what the form shows. A client-side rule would pre-empt that message
   * with a vaguer one.
   */
  async grantTenantCreator(email: string): Promise<StaffMember> {
    const res = await controlApiRequest<MemberResponse>({
      method: 'POST',
      path: TENANT_CREATOR,
      body: { email: email.trim() },
    });

    // controlApiRequest yields undefined for a 204 and null for an empty body,
    // and the grant has succeeded either way. Build the member from the
    // submitted address so mutationFn resolves and the list is invalidated,
    // rather than throwing a TypeError into the modal (PR #630 review).
    return toMember({ email: email.trim(), ...(res ?? {}) });
  },

  /** `204`, idempotent: revoking a non-member is not an error. */
  async revokeTenantCreator(email: string): Promise<void> {
    await controlApiRequest<void>({
      method: 'DELETE',
      path: `${TENANT_CREATOR}/${encodeURIComponent(email)}`,
    });
  },
};
